import React, { useCallback , useEffect } from "react";
import { Card, Row, Col, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setFilters } from "../indirectIncomeBalanceSlice";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { SearchOutlined } from "@ant-design/icons";
import {
  fetchIndirectIncomeBalance,
  fetchIndirectIncomeAccountsBySearch,
} from "../indirectIncomeBalanceThunk";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const IndirectIncomeBalanceFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.indirectIncomeBalance);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useDefaultDateRange(setFilters, filters, "today");

  useEffect(() => {
    // Drill-down with a full range (e.g. from the Trial Balance). "Date Wise"
    // is required here: the thunk only sends the dates for that filter type.
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");
    if (dateFrom && dateTo) {
      const dateFilters = {
        filterType: "Date Wise",
        accId: null,
        fromDate: dateFrom,
        toDate: dateTo,
      };
      dispatch(setFilters(dateFilters));
      dispatch(
        fetchIndirectIncomeBalance({ filters: { ...filters, ...dateFilters } })
      );
      return;
    }

    const path = pathname.split("/")[3];
    if (path) {
      handleFilterChange("filterType", "Date Wise");
      handleFilterChange("toDate", path);
      handleSubmit();
    }
  }, []);

  const handleFilterChange = (field, value) => {
    if (field === "filterType") {
      dispatch(setFilters({ accId: null }));
    }
    dispatch(setFilters({ [field]: value }));
  };

  const searchAccount = useCallback(
    (text) => dispatch(fetchIndirectIncomeAccountsBySearch(text)),
    [dispatch]
  );

  const handleSubmit = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }
    dispatch(fetchIndirectIncomeBalance({ filters })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch report ❌");
      }
    });
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row
        gutter={[16, 16]}
        align="middle"
        wrap
        style={{ alignItems: "center" }}
      >
        {/* Filter Type */}
        <Col xs={24} sm={12} md={4} lg={6} xl={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              {
                sale_id: "All Income Account",
                display_text: "All Income Account",
              },
              { sale_id: "Account Wise", display_text: "Account Wise" },
              { sale_id: "Date Wise", display_text: "Date Wise" },
            ]}
            value={
              filters.filterType
                ? { value: filters.filterType, label: filters.filterType }
                : undefined
            }            
            onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("filterType", null);
                return;
              } 
              handleFilterChange("filterType", obj?.sale_id);
            }}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {/* Account dropdown when Account Wise */}
        {filters.filterType === "Account Wise" && (
          <Col xs={24} sm={12} md={4} lg={6} xl={4}>
            <label>Search Account</label>
            <SearchListAutoComplete
              entityType="account"
              searchThunk={searchAccount}
              resultSelector={(state) =>
                state.indirectIncomeBalance.incomeAccounts
              }
              onSelect={(obj) => {
                if(!obj) {
                handleFilterChange("accId", null);
                return;
              } 
                handleFilterChange("accId", obj?.sale_id);
              }}
              placeholder="Account"
              showAddNew={false}
            />
          </Col>
        )}
        {filters.filterType === "Date Wise" && (
          <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
          handleFilterChange("fromDate", fromDate);
          handleFilterChange("toDate", toDate);
          }}
        />
        )}

        <Col
          xs={24}
          sm={12}
          md={8}
          lg={6}
          xl={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            block
            data-testid="report-btn"
          >
            Report
          </Button>
        </Col>
      </Row>
        <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
      @media (min-width: 768px) {
  :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
    max-width: none !important;
    flex: none !important;
  }
}
  `}</style>
    </Card>
  );
};

export default IndirectIncomeBalanceFilters;
